/**
 * RealNavigationProvider - React Router integration
 *
 * Maps flow view names to actual demo routes and navigates using
 * React Router's navigate() function.
 */

import { NavigationProvider } from '../types';

type NavigateFunction = (to: string, options?: { replace?: boolean }) => void;

/** Map flow view names to demo route paths */
const VIEW_ROUTE_MAP: Record<string, string> = {
  'dashboard': '/demo/dashboard',
  'vault': '/demo/vault',
  'deal-page': '/demo/deal',
  'deal-creation': '/demo/new-deal',
  'contract-analysis': '/demo/analysis',
  'deal-pipeline': '/demo/deal-pipeline',
  'calendar': '/demo/calendar',
  'inbox': '/demo/inbox',
  'delivery-manager': '/demo/delivery-manager',
  'portfolio': '/demo/portfolio',
  'integrations': '/demo/integrations',
  'organizations': '/demo/organizations',
};

export class RealNavigationProvider implements NavigationProvider {
  private navigate: NavigateFunction;

  constructor(navigate: NavigateFunction) {
    this.navigate = navigate;
  }

  async navigateToView(view: string, params?: Record<string, any>): Promise<void> {
    const basePath = VIEW_ROUTE_MAP[view];
    if (!basePath) {
      console.warn(`[RealNavigation] Unknown view: ${view}`);
      return;
    }

    let path = basePath;

    // Handle parameterized routes
    if (view === 'deal-page' && params?.dealId) {
      path = `${basePath}/${params.dealId}`;
    } else if (view === 'contract-analysis' && params?.contractId) {
      path = `${basePath}/${params.contractId}`;
    }

    this.navigate(path);
  }

  async navigateTo(path: string): Promise<void> {
    this.navigate(path);
  }

  async goBack(): Promise<void> {
    window.history.back();
  }

  getCurrentRoute() {
    return {
      path: window.location.pathname,
      view: Object.entries(VIEW_ROUTE_MAP).find(
        ([, route]) => window.location.pathname.startsWith(route)
      )?.[0],
      params: Object.fromEntries(new URLSearchParams(window.location.search)),
    };
  }
}
